// feat: closed enum of diagram IR kinds per kit, versioned in shared
// The compiler maps these kinds to mxCell XML styles. Unknown kinds on write
// are rejected with the allowed list. `raw` is an internal passthrough used
// by the importer to preserve cells it could not classify.

import type { DiagramKit } from '../types/diagram.js';

export const AWS_KINDS = [
  'aws.cloud',
  'aws.vpc',
  'aws.security_group',
  'aws.users',
  'aws.api_gateway',
  'aws.iam',
  'aws.lambda',
  'aws.eventbridge',
  'aws.rds',
  'aws.aurora_postgresql',
  'aws.vpc_endpoints',
  'aws.secrets_manager',
  'aws.kms',
  'aws.sagemaker',
  'aws.sagemaker_model',
  'aws.kinesis_data_firehose',
  'aws.s3',
  'aws.glue',
  'aws.athena',
  'aws.parameter_store',
  'aws.cloudwatch',
  'aws.xray',
  'aws.cloudformation',
  'aws.codepipeline',
  'aws.codebuild',
] as const;

export const UML_KINDS = [
  'uml.class',
  'uml.interface',
  'uml.abstract',
  'uml.enum',
  'uml.package',
  'uml.frame',
  'uml.component',
  'uml.module',
  'uml.node',
  'uml.provided',
  'uml.required',
  'uml.artifact',
  'uml.actor',
  'uml.usecase',
  'uml.system',
  'uml.note',
  'uml.constraint',
  'uml.state',
  'uml.state_composite',
  'uml.initial',
  'uml.final',
  'uml.decision',
  'uml.fork',
  'uml.activity',
  'uml.object',
  'uml.lifeline',
  'uml.activation',
  'uml.boundary',
  'uml.control',
  'uml.entity',
  'uml.collaboration',
  'uml.port',
  'uml.pin_in',
  'uml.pin_out',
  'uml.destroy',
  'uml.generalization',
  'uml.realization',
  'uml.association',
  'uml.aggregation',
  'uml.composition',
  'uml.dependency',
  'uml.include',
  'uml.extend',
  'uml.message',
  'uml.message_async',
  'uml.message_return',
  'uml.transition',
] as const;

export const BPMN_KINDS = [
  'bpmn.start',
  'bpmn.start_message',
  'bpmn.start_timer',
  'bpmn.start_conditional',
  'bpmn.start_link',
  'bpmn.start_multiple',
  'bpmn.intermediate',
  'bpmn.intermediate_message',
  'bpmn.intermediate_timer',
  'bpmn.intermediate_error',
  'bpmn.intermediate_compensation',
  'bpmn.intermediate_cancel',
  'bpmn.intermediate_link',
  'bpmn.intermediate_rule',
  'bpmn.intermediate_multiple',
  'bpmn.end',
  'bpmn.end_message',
  'bpmn.end_error',
  'bpmn.end_cancel',
  'bpmn.end_compensation',
  'bpmn.end_link',
  'bpmn.end_multiple',
  'bpmn.end_terminate',
  'bpmn.task',
  'bpmn.user_task',
  'bpmn.service_task',
  'bpmn.script_task',
  'bpmn.manual_task',
  'bpmn.business_rule_task',
  'bpmn.subprocess',
  'bpmn.gateway',
  'bpmn.gateway_parallel',
  'bpmn.gateway_inclusive',
  'bpmn.gateway_event',
  'bpmn.gateway_complex',
  'bpmn.pool',
  'bpmn.lane',
  'bpmn.data_object',
  'bpmn.data_store',
  'bpmn.annotation',
  'bpmn.group',
] as const;

export const UML_EDGE_KINDS = new Set([
  'uml.generalization',
  'uml.realization',
  'uml.association',
  'uml.aggregation',
  'uml.composition',
  'uml.dependency',
  'uml.include',
  'uml.extend',
  'uml.message',
  'uml.message_async',
  'uml.message_return',
  'uml.transition',
]);

/** All closed-enum kinds, per kit. `raw` is a passthrough, not authored. */
export const KIT_KINDS: Record<DiagramKit, readonly string[]> = {
  aws: AWS_KINDS,
  uml: UML_KINDS,
  bpmn: BPMN_KINDS,
};

export const ALL_DIAGRAM_KINDS: readonly string[] = [...AWS_KINDS, ...UML_KINDS, ...BPMN_KINDS];

export function isDiagramKind(kind: string): boolean {
  return ALL_DIAGRAM_KINDS.includes(kind);
}

export function kitOfKind(kind: string): DiagramKit | undefined {
  for (const kit of Object.keys(KIT_KINDS) as DiagramKit[]) {
    if (KIT_KINDS[kit].includes(kind)) return kit;
  }
  return undefined;
}
